/**
 * Lane FACE-EXPR (ON in the game since 10-11; review tools can turn it off): the faces' eyes, brows and
 * mouths built per view (front, 3/4, profile) from one description, so a resting look (appearance.ts FACES: calm, stern,
 * gentle, cold, bright, sly) and an expression change the face the same way in every view.
 *
 * - Eyes: an almond opening per eye (inner -> outer corner) under a lash line; lids are cut per column (a glare cuts the
 *   inner end, sadness the outer end, a smile lifts the lower lid), the iris/pupil/one catchlight placed by gaze. Closed
 *   eyes are lines (shut, smile arc, pain chevron). Men: one even lash row (+ a second over the outer half), a lower-lid
 *   crease; women: a heavier lash, a flick past the outer corner, a lower lash on the outer side.
 * - Brows: a polyline from the inner to the outer end per view, moved by the inner end, the outer end and an arch; men two
 *   rows at the inner end, women one row, kept clear of the fringe (a row of skin above) and of the lash.
 * - Mouths: one front stamp per shape; 3/4 drops columns on the far side; profile keeps the front half at the face edge.
 * Faces skull (skullB3.ts) and noses are untouched. Hooked into faceB3.ts (before its memo: `proposedFeatures`, `featureKey`). Research: docs/research/FACE_EXPR.md.
 */
import type { View } from '../../sprites';

/** The six resting looks (appearance.ts FACES ids). */
export const REST_FACES = ['calm', 'stern', 'gentle', 'cold', 'bright', 'sly'] as const;
export type RestFace = (typeof REST_FACES)[number];

/** The proposed expressions: combat, then social, then the everyday ones. */
export const FACE_EXPRS = [
  'neutral',
  'focus',
  'effort',
  'shout',
  'pain',
  'rage',
  'dazed',
  'ko',
  'victory',
  'smile',
  'laugh',
  'sad',
  'surprised',
  'angry',
  'shy',
  'talk',
  'blink',
] as const;
export type FaceExpr = (typeof FACE_EXPRS)[number];

export const FACE_EXPR_LABELS: Record<FaceExpr, string> = {
  neutral: 'ปกติ',
  focus: 'จดจ่อ (ตั้งท่า/ตั้งรับ)',
  effort: 'ออกแรง (ตอนฟัน)',
  shout: 'ตะโกนโจมตี',
  pain: 'เจ็บ (โดนตี)',
  rage: 'โทสะ (ท่าใหญ่)',
  dazed: 'มึน (ติดสตัน)',
  ko: 'หมดสติ',
  victory: 'ชนะ',
  smile: 'ยิ้ม',
  laugh: 'หัวเราะ',
  sad: 'เศร้า',
  surprised: 'ตกใจ',
  angry: 'โกรธ',
  shy: 'เขิน',
  talk: 'พูด',
  blink: 'กะพริบ',
};

/** ON in the game (user, 10-11). Review tools turn it off to draw the old faces. */
let ON = true;
/** Review tools: the new faces on or off (off = the old faces). */
export function setFaceExprOn(v: boolean): void {
  ON = v;
}
let LAB_EXPR: FaceExpr | null = null;
let LAB_REST: RestFace = 'calm';
/** Review tools: a fixed proposed expression and resting look. */
export function setLabFace(expr: FaceExpr | null, rest: RestFace = 'calm'): void {
  LAB_EXPR = expr;
  LAB_REST = rest;
}

type Sex = 'male' | 'female';
export type FPx = { x: number; y: number; col: string };
/** Colour letters: K lash, W white, w white in shade, I iris, L iris light, P pupil, S shine, B brow, b brow shade,
 * c crease, m mouth line, n soft mouth end, M inside of the mouth, t teeth, R lip, r lip shade, h blush, T tear. */
export type Colours = Record<'K' | 'W' | 'w' | 'I' | 'L' | 'P' | 'S' | 'B' | 'b' | 'c' | 'm' | 'n' | 'M' | 't' | 'R' | 'r' | 'h' | 'T', string>;

// ── geometry per sex and view (fine px from the head joint's fine corner, as faceB3.ts) ──

/** One eye: inner corner column `x0`, `dir` from inner to outer (+1 right), `w` columns, lash row `top`, `open` rows. */
interface EyeGeo {
  x0: number;
  dir: 1 | -1;
  w: number;
  top: number;
  open: number;
  iris: number;
  /** Foreshortened (3/4 far eye, profile): no lower lash, a lid cut of at most a row. */
  far?: boolean;
  /** Men head-on: a second lash row over the outer half (turned, it would touch the brow). */
  lash2?: boolean;
}
/** One brow: columns from the inner end (x0, dir) and their rows at rest. */
interface BrowGeo {
  x0: number;
  dir: 1 | -1;
  rows: readonly number[];
}
interface ViewGeo {
  eyes: EyeGeo[];
  brows: BrowGeo[];
  /** Mouth: front = centre column (the face's centre line is between −1 and 0), 3/4 = left column, profile = right column. */
  mouth: { x: number; y: number };
  /** Where the eyes look by default (−1 left, 0 ahead, 1 right). */
  gaze: number;
}

const GEO: Record<Sex, Record<'front' | 'front3' | 'side', ViewGeo>> = {
  male: {
    front: {
      eyes: [{ x0: 2, dir: 1, w: 6, top: 2, open: 3, iris: 3, lash2: true }],
      brows: [{ x0: 2, dir: 1, rows: [-2, -2, -2, -2, -2, -2, -2] }],
      mouth: { x: -0.5, y: 11 },
      gaze: 0,
    },
    front3: {
      eyes: [
        { x0: 0, dir: -1, w: 6, top: 2, open: 3, iris: 3 },
        { x0: 5, dir: 1, w: 4, top: 2, open: 2, iris: 2, far: true },
      ],
      brows: [
        { x0: 0, dir: -1, rows: [-1, -1, -1, -1, -1, 0] },
        { x0: 5, dir: 1, rows: [-1, -1, -1, -1] },
      ],
      mouth: { x: 2, y: 11 },
      gaze: 1,
    },
    side: {
      eyes: [{ x0: 8, dir: -1, w: 4, top: 2, open: 3, iris: 2, far: true }],
      brows: [{ x0: 8, dir: -1, rows: [0, 0, 0, 0] }],
      mouth: { x: 12, y: 10 },
      gaze: 1,
    },
  },
  female: {
    front: {
      eyes: [{ x0: 2, dir: 1, w: 6, top: 3, open: 3, iris: 3 }],
      brows: [{ x0: 2, dir: 1, rows: [1, 0, 0, 0, 0, 1] }],
      mouth: { x: -0.5, y: 11 },
      gaze: 0,
    },
    front3: {
      eyes: [
        { x0: 1, dir: -1, w: 6, top: 4, open: 3, iris: 3 },
        { x0: 6, dir: 1, w: 5, top: 4, open: 2, iris: 2, far: true },
      ],
      brows: [
        { x0: 0, dir: -1, rows: [1, 0, 0, 0, 0, 1] },
        { x0: 6, dir: 1, rows: [0, 0, 0, 1] },
      ],
      mouth: { x: 3, y: 11 },
      gaze: 1,
    },
    side: {
      eyes: [{ x0: 9, dir: -1, w: 5, top: 3, open: 3, iris: 2, far: true }],
      brows: [{ x0: 8, dir: -1, rows: [0, 0, 0, 1] }],
      mouth: { x: 11, y: 11 },
      gaze: 1,
    },
  },
};

// ── eye states ──

/** How the lids sit: `cutTop(t)` rows cut off the top of the opening at t (0 inner .. 1 outer), `lift(t)` off the
 * bottom; `raise` lifts the lash a row (whites over the iris); `irisW` −1 a smaller iris (rage, surprise); `gx`, `gy`
 * move the iris; `line` draws a closed eye instead. */
interface EyeState {
  cutTop?: (t: number) => number;
  lift?: (t: number) => number;
  raise?: boolean;
  irisW?: number;
  gx?: number;
  gy?: number;
  line?: 'shut' | 'arc' | 'arc2' | 'chevron' | 'droop';
  /** A second catchlight (bright eyes). */
  shine2?: boolean;
}

const EYE: Record<string, EyeState> = {
  open: {},
  round: { lift: () => 0, shine2: true },
  wide: { raise: true, shine2: true },
  narrow: { cutTop: () => 1 },
  cold: { cutTop: () => 1, irisW: -1 },
  glare: { cutTop: (t) => (t < 0.4 ? 2 : t < 0.75 ? 1 : 0) },
  hardGlare: { cutTop: (t) => (t < 0.5 ? 2 : 1), lift: (t) => (t > 0.3 && t < 0.8 ? 1 : 0) },
  sternLid: { cutTop: (t) => (t < 0.4 ? 1 : 0) },
  gentle: { cutTop: (t) => (t > 0.7 ? 1 : 0), lift: (t) => (t > 0.25 && t < 0.75 ? 1 : 0) },
  slyL: { cutTop: () => 1, lift: (t) => (t > 0.3 ? 1 : 0), gx: -1 },
  slyR: { cutTop: () => 1, gx: -1 },
  rage: { raise: true, irisW: -1, cutTop: (t) => (t < 0.3 ? 1 : 0) },
  surprised: { raise: true, irisW: -1 },
  // One row off the outer half, the iris low (review r1: a two-step cut read busy head-on).
  sad: { cutTop: (t) => (t > 0.5 ? 1 : 0), gy: 1 },
  shy: { cutTop: () => 1, gy: 1, gx: -1 },
  dazedA: { cutTop: () => 1, gx: -1, gy: 1 },
  dazedB: { cutTop: () => 1, gx: 1, gy: -1 },
  shut: { line: 'shut' },
  droop: { line: 'droop' },
  arc: { line: 'arc' },
  arc2: { line: 'arc2' },
  chevron: { line: 'chevron' },
};

/** Brow moves: inner end and outer end rows down (+) or up (−), arch lifts the middle, `thick` adds a row on women. */
interface BrowState {
  inner: number;
  outer: number;
  arch?: number;
  thick?: boolean;
  /** Only this brow index (front: 0 = screen right, 1 = screen left) gets `alt` instead. */
  alt?: { inner: number; outer: number; arch?: number };
}

interface FaceState {
  eyes: [string, string?];
  brow: BrowState;
  mouth: string;
  extra?: ('blush' | 'tear')[];
}

const B = (inner: number, outer: number, arch = 0, thick = false): BrowState => ({ inner, outer, arch, thick });

/** The resting looks: eyes, brows and mouth at rest. */
const REST: Record<RestFace, Record<Sex, FaceState>> = {
  calm: { male: { eyes: ['open'], brow: B(0, 0), mouth: 'calm' }, female: { eyes: ['open'], brow: B(0, 0), mouth: 'calm' } },
  stern: {
    male: { eyes: ['sternLid'], brow: B(1, 0, 0), mouth: 'stern' },
    female: { eyes: ['sternLid'], brow: B(1, 0, 0), mouth: 'stern' },
  },
  gentle: {
    male: { eyes: ['gentle'], brow: B(-1, 1, 0), mouth: 'gentle' },
    female: { eyes: ['gentle'], brow: B(-1, 1, 0), mouth: 'gentle' },
  },
  cold: { male: { eyes: ['cold'], brow: B(0, -1), mouth: 'cold' }, female: { eyes: ['cold'], brow: B(0, -1), mouth: 'cold' } },
  bright: {
    male: { eyes: ['round'], brow: B(-1, -1, 1), mouth: 'bright' },
    female: { eyes: ['round'], brow: B(-1, -1, 1), mouth: 'bright' },
  },
  sly: {
    male: { eyes: ['slyR', 'slyL'], brow: { inner: 0, outer: 0, alt: { inner: -1, outer: -2 } }, mouth: 'smirk' },
    female: { eyes: ['slyR', 'slyL'], brow: { inner: 0, outer: 0, alt: { inner: -1, outer: -2 } }, mouth: 'smirk' },
  },
};

/** The expressions (shared by every look; the look shows at rest, in talk and in blink). */
const EXPRS: Record<Exclude<FaceExpr, 'neutral' | 'talk' | 'blink'>, FaceState> = {
  focus: { eyes: ['glare'], brow: B(2, 0), mouth: 'firm' },
  effort: { eyes: ['hardGlare'], brow: B(2, 0, 0, true), mouth: 'grit' },
  shout: { eyes: ['glare'], brow: B(3, 0, 0, true), mouth: 'shout' },
  pain: { eyes: ['chevron'], brow: B(-2, 1), mouth: 'grimace', extra: [] },
  rage: { eyes: ['rage'], brow: B(3, -1, 0, true), mouth: 'roar' },
  dazed: { eyes: ['dazedA', 'dazedB'], brow: B(-1, 1), mouth: 'slack' },
  ko: { eyes: ['droop'], brow: B(-1, 1), mouth: 'ko' },
  victory: { eyes: ['gentle'], brow: B(-1, 0, 1), mouth: 'victory' },
  smile: { eyes: ['arc'], brow: B(-1, 0, 1), mouth: 'smile' },
  laugh: { eyes: ['arc2'], brow: B(-1, -1, 1), mouth: 'laugh', extra: ['blush'] },
  sad: { eyes: ['sad'], brow: B(-2, 1), mouth: 'sad', extra: ['tear'] },
  surprised: { eyes: ['surprised'], brow: B(-2, -2, 1), mouth: 'oh' },
  angry: { eyes: ['glare'], brow: B(3, 0, 0, true), mouth: 'snarl' },
  shy: { eyes: ['shy'], brow: B(-1, 1), mouth: 'shy', extra: ['blush'] },
};

/** Man's victory: a closed smirk; woman's: a soft smile (sexes apart). */
function stateOf(sex: Sex, rest: RestFace, e: FaceExpr): FaceState {
  const r = REST[rest][sex];
  if (e === 'neutral') return r;
  if (e === 'talk') return { ...r, mouth: 'talk' };
  if (e === 'blink') return { ...r, eyes: [rest === 'sly' || rest === 'cold' ? 'shut' : 'shut'] };
  const s = EXPRS[e];
  if (e === 'victory' && sex === 'female') return { ...s, mouth: 'smile' };
  return s;
}

// ── mouths (front stamps; letters as Colours; `.` empty) ──

const MOUTH: Record<string, Record<Sex, readonly string[]>> = {
  calm: { male: ['nmmmn'], female: ['nmmn', '.RR.'] },
  stern: { male: ['.mmmm.', 'n....n'], female: ['.mmm.', 'n.R.n'] },
  gentle: { male: ['n....n', '.mmmm.'], female: ['n...n', '.mRm.'] },
  cold: { male: ['mmm'], female: ['mmm', '.r.'] },
  bright: { male: ['m....m', '.mMMm.'], female: ['m...m', '.mMm.', '..R..'] },
  smirk: { male: ['.....m', 'nmmmm.'], female: ['....m', 'nmmm.', '.RR..'] },
  talk: { male: ['.mm.', 'mMMm', '.mm.'], female: ['.mm.', 'mMMm', '.RR.'] },
  firm: { male: ['mmmmm'], female: ['mmmm', '.rr.'] },
  grit: { male: ['mmmmmm', 'mttttm', '.mmmm.'], female: ['mmmmm', 'mtttm', '.RRR.'] },
  shout: { male: ['.mmmmm.', 'mtttttm', 'mMMMMMm', 'mMMMMMm', '.mmmmm.'], female: ['.mmmm.', 'mttttm', 'mMMMMm', 'mMMMMm', '.RRRR.'] },
  grimace: { male: ['m.mmm.m', '.mtttm.', 'm.....m'], female: ['m.mm.m', '.mttm.', 'm....m'] },
  roar: { male: ['mmmmmmm', 'MtttttM', 'MMMMMMM', 'mMMMMMm', '.mmmmm.'], female: ['mmmmmm', 'MttttM', 'MMMMMM', 'mMMMMm', '.RRRR.'] },
  slack: { male: ['.mm..', 'mMMm.', '.mm..'], female: ['.mm..', 'mMMm.', '.RR..'] },
  ko: { male: ['.mmm.', 'm.M.m'], female: ['.mm.', 'mMMm', '.RR.'] },
  victory: { male: ['......m', 'nmmmmm.'], female: ['m...m', '.mRm.'] },
  smile: { male: ['m....m', '.mmmm.'], female: ['m....m', '.mmmm.', '..RR..'] },
  laugh: { male: ['mmmmmm', 'mttttm', 'mMMMMm', '.mMMm.'], female: ['mmmmm', 'mtttm', 'mMMMm', '.RRR.'] },
  sad: { male: ['.mmmm.', 'm....m'], female: ['.mmm.', 'm.R.m'] },
  oh: { male: ['.mm.', 'mMMm', 'mMMm', '.mm.'], female: ['.mm.', 'mMMm', 'mMMm', '.RR.'] },
  snarl: { male: ['mmmmmm', 'mttttm', 'm....m'], female: ['mmmmm', 'mtttm', 'm.R.m'] },
  shy: { male: ['nmn'], female: ['nmn', '.R.'] },
};

/**
 * `left`: an L view (drawn as its right view, then flipped on screen): the stamp is mirrored first, so an asymmetric
 * mouth (smirk) keeps its raised corner on the same side of the face after the flip.
 */
function mouthPx(shape: readonly string[], view: 'front' | 'front3' | 'side', at: { x: number; y: number }, left = false): { x: number; y: number; ch: string }[] {
  const w = Math.max(...shape.map((r) => r.length));
  let rows = shape.map((r) => r.padEnd(w, '.'));
  if (left) rows = rows.map((r) => [...r].reverse().join(''));
  if (view === 'front3') {
    // The far side (right) foreshortened: a column right of the middle dropped (two on wide mouths); the far corner
    // stays (review r1: it was dropped, so smiles lost a corner).
    const mid = Math.floor(w / 2);
    const drop = w >= 7 ? [mid + 1, mid + 3] : w >= 5 ? [mid + 1] : [];
    const keep = [...Array(w).keys()].filter((c) => !drop.includes(c));
    rows = rows.map((r) => keep.map((c) => r[c]).join(''));
  } else if (view === 'side') {
    // Profile: the near half (the stamp's left half: facing right, the face's right side shows), its corner toward the
    // cheek and its middle at the face's edge (review r1: the corner was at the edge).
    rows = rows.map((r) => r.slice(0, w - Math.floor(w / 2)));
  }
  const ww = rows[0]!.length;
  const x0 = view === 'front' ? Math.round(at.x + 0.5 - ww / 2) : view === 'front3' ? at.x : at.x - ww + 1;
  // Taller mouths open downward from the line; shapes whose first row is a corner row (smiles) sit a row higher.
  // (Counted without the dots: `trim` kept them, so smiles and the smirk sat a row low: review r1.)
  const y0 = at.y - (rows[0]!.replace(/\./g, '').length <= 2 && rows.length > 1 ? 1 : 0);
  const out: { x: number; y: number; ch: string }[] = [];
  rows.forEach((r, y) => {
    for (let x = 0; x < r.length; x++) if (r[x] !== '.') out.push({ x: x0 + x, y: y0 + y, ch: r[x]! });
  });
  return out;
}

// ── eyes ──

function eyePx(g: EyeGeo, st: EyeState, sex: Sex, gaze: number, shineLeft: boolean): { x: number; y: number; ch: string }[] {
  const out: { x: number; y: number; ch: string }[] = [];
  const put = (i: number, y: number, ch: string) => out.push({ x: g.x0 + g.dir * i, y, ch });
  const n = g.w;
  const tOf = (i: number) => (n > 1 ? i / (n - 1) : 0);
  const female = sex === 'female';
  if (st.line) {
    const y = g.top + Math.floor(g.open / 2) + 1;
    for (let i = 0; i < n; i++) {
      const t = tOf(i);
      let dy = 0;
      if (st.line === 'arc' || st.line === 'arc2') dy = i === 0 || i === n - 1 ? 0 : -1;
      if (st.line === 'droop') dy = i === 0 || i === n - 1 ? -1 : 0;
      if (st.line === 'chevron') {
        // Pain: two strokes meeting at the inner corner, opening to the outer corner.
        const k = Math.round(t * (g.far ? 1 : 2));
        put(i, y - k, 'K');
        if (i > 0) put(i, y + k, 'K');
        continue;
      }
      put(i, y + dy, 'K');
      if (st.line === 'arc2' && i > 0 && i < n - 1) put(i, y + dy - 1, 'K');
    }
    if (female && !g.far && st.line !== 'chevron') put(n, g.top + Math.floor(g.open / 2) + (st.line === 'droop' ? 0 : 1), 'K');
    return out;
  }
  // The opening per column: rows [a, b], almond corners one row shallower.
  const cols: { a: number; b: number }[] = [];
  for (let i = 0; i < n; i++) {
    const t = tOf(i);
    const corner = i === 0 || i === n - 1 ? 1 : 0;
    // Small or foreshortened eyes are cut a row at most (more left a scribble: review r1).
    const cut = Math.min(st.cutTop?.(t) ?? 0, g.far || g.open <= 2 ? 1 : 2);
    const a = g.top + 1 + cut + (st.raise ? -1 : 0);
    const b = g.top + g.open - corner - (st.lift?.(t) ?? 0);
    cols.push({ a, b });
  }
  // Lash: over the opening's top in each column (a lid cut moves it down with the cut).
  for (let i = 0; i < n; i++) put(i, Math.min(cols[i]!.a - 1, cols[i]!.a - 1), 'K');
  // Heavier lash: men a second row over the outer half, women over the outer two thirds, plus a flick.
  for (let i = 0; i < n; i++) {
    const t = tOf(i);
    if ((!female && t >= 0.5 && g.lash2) || (female && t >= 0.34)) put(i, cols[i]!.a - 2, 'K');
  }
  if (female) put(n, cols[n - 1]!.a - 2, 'K');
  // Iris placement: centred, moved by gaze (+1 = toward screen right).
  const iw = Math.max(1, g.iris + (st.irisW ?? 0));
  const gx = (st.gx ?? 0) + gaze;
  // Column index range of the iris (in screen terms, then mapped to i).
  const left = Math.min(g.x0, g.x0 + g.dir * (n - 1));
  let ix0 = left + Math.floor((n - iw) / 2) + gx;
  ix0 = Math.max(left + 1, Math.min(left + n - 1 - iw, ix0));
  if (n <= 4) ix0 = Math.max(left, Math.min(left + n - iw, Math.floor(left + (n - iw) / 2 + gx * 0.5 + 0.5)));
  const irisTop = g.top + 1 + (st.gy ?? 0) > 0 ? g.top + 1 + Math.max(0, st.gy ?? 0) : g.top + 1;
  for (let i = 0; i < n; i++) {
    const x = g.x0 + g.dir * i;
    const { a, b } = cols[i]!;
    for (let y = a; y <= b; y++) {
      const inIris = x >= ix0 && x < ix0 + iw && y >= irisTop;
      let ch = inIris ? 'I' : y === a ? 'w' : 'W';
      if (inIris && y === b && b > irisTop) ch = 'L';
      out.push({ x, y, ch });
    }
  }
  // Pupil: the iris's middle column(s), second row; shine on the iris's top row toward the light.
  const pr = irisTop + 1;
  const px = ix0 + Math.floor((iw - 1) / 2);
  for (const p of out) if (p.y === pr && (p.x === px || (iw >= 3 && !female ? false : iw >= 3 && p.x === px + 1)) && p.ch === 'I') p.ch = 'P';
  if (iw >= 1) {
    const sx = shineLeft ? ix0 : ix0 + iw - 1;
    const s = out.find((p) => p.x === sx && p.y === irisTop && (p.ch === 'I' || p.ch === 'L'));
    if (s && iw >= 2) s.ch = 'S';
    if (st.shine2) {
      const s2 = out.find((p) => p.x === (shineLeft ? ix0 + iw - 1 : ix0) && p.y === irisTop + 2 && (p.ch === 'I' || p.ch === 'L'));
      if (s2) s2.ch = 'S';
    }
  }
  // Lower lid: men a crease under the middle; women a lower lash on the outer side.
  for (let i = 0; i < n; i++) {
    const t = tOf(i);
    const { b } = cols[i]!;
    if (female && !g.far && t >= 0.6) put(i, b + 1, 'K');
    else if (!female && !g.far && t > 0.2 && t < 0.8) put(i, b + 1, 'c');
  }
  return out;
}

// ── brows ──

function browPx(g: BrowGeo, s: { inner: number; outer: number; arch?: number; thick?: boolean }, sex: Sex, eyeTops: Map<number, number>) {
  const out: { x: number; y: number; ch: string }[] = [];
  const n = g.rows.length;
  const female = sex === 'female';
  const ys: number[] = [];
  // A strong tilt (anger) from a level base: the resting droop of the tail would bend it into a V (review r1).
  const base = s.inner - s.outer >= 2 ? Math.min(...g.rows) : null;
  for (let i = 0; i < n; i++) {
    const t = n > 1 ? i / (n - 1) : 0;
    const arch = s.arch ? Math.round(s.arch * Math.sin(Math.PI * t)) : 0;
    ys.push((base ?? g.rows[i]!) + Math.round(s.inner * (1 - t) + s.outer * t) - arch);
  }
  // Women: never above row −1 (the fringe ends at −2; a raised brow there vanished into the hair: review r1).
  if (female) for (let i = 0; i < n; i++) ys[i] = Math.max(-1, ys[i]!);
  // Men: never above row −4, a row of skin under the hairline (review r1: surprised/bright brows touched it).
  else for (let i = 0; i < n; i++) ys[i] = Math.max(-4, ys[i]!);
  // A row of skin over the lash (review r1: brows touching the lash read as scribbles at 3/4 and in profile).
  const lashAt = (x: number) => eyeTops.get(x) ?? eyeTops.get(x - 1) ?? eyeTops.get(x + 1);
  for (let i = 0; i < n; i++) {
    const lash = lashAt(g.x0 + g.dir * i);
    if (lash !== undefined) ys[i] = Math.min(ys[i]!, lash - 2);
  }
  // Steps of at most a pixel between neighbours (the brow stays one piece).
  for (let i = 1; i < n; i++) ys[i] = Math.max(ys[i - 1]! - 1, Math.min(ys[i - 1]! + 1, ys[i]!));
  for (let i = 0; i < n; i++) {
    const x = g.x0 + g.dir * i;
    const t = n > 1 ? i / (n - 1) : 0;
    const y = ys[i]!;
    out.push({ x, y, ch: 'B' });
    // The second row; also where the next column steps down, so the brow stays joined.
    // Men: two rows but the outer tip, following the top row (review r1: a partial second row left holes on arches).
    const second = female ? !!s.thick && (t < 0.6 || (i + 1 < n && ys[i + 1]! > y)) : t < 0.85 || !!s.thick;
    if (!second) continue;
    // Under the brow only while a row of skin stays over the lash (a row above read as stray pixels).
    const lash = lashAt(x);
    if (lash === undefined || y + 1 <= lash - 2) out.push({ x, y: y + 1, ch: 'b' });
  }
  return out;
}

// ── the hook (faceB3.ts) ──

const mapExpr = (e: string): FaceExpr =>
  e === 'angry' ? 'focus' : e === 'hurt' ? 'pain' : (FACE_EXPRS as readonly string[]).includes(e) ? (e as FaceExpr) : 'neutral';

/** The game's say for a frame (faceExprWire.ts): its resting look and the expression its pose asks for. */
export interface FeatureOpts {
  rest?: RestFace;
  auto?: FaceExpr | null;
}

/** The proposal's key for faceB3's memo (empty when off). */
export function featureKey(o: FeatureOpts = {}): string {
  return ON ? `x:${LAB_EXPR ?? o.auto ?? ''}:${LAB_EXPR ? LAB_REST : (o.rest ?? 'calm')}` : '';
}

/**
 * Eyes, brows and mouth for a face (null when off, for elders, or for views without a face). `expr` is today's
 * expression (mapped when neither the lab nor the pose (`o.auto`) sets one); `left` mirrors the light for the left views.
 */
export function proposedFeatures(
  sex: Sex,
  age: string | undefined,
  view: View,
  expr: string,
  left: boolean,
  col: Colours,
  o: FeatureOpts = {},
): { eyesBrows: FPx[]; mouth: FPx[]; blush: boolean } | null {
  if (!ON || age === 'elder') return null;
  if (view !== 'front' && view !== 'front3' && view !== 'side') return null;
  const e = LAB_EXPR ?? o.auto ?? mapExpr(expr);
  const st = stateOf(sex, LAB_EXPR ? LAB_REST : (o.rest ?? 'calm'), e);
  const geo = GEO[sex][view];
  const eyes: { x: number; y: number; ch: string }[] = [];
  const tops = new Map<number, number>();
  const eyeList: { g: EyeGeo; name: string }[] = [];
  if (view === 'front') {
    const g = geo.eyes[0]!;
    eyeList.push({ g, name: st.eyes[0] });
    eyeList.push({ g: { ...g, x0: -1 - g.x0, dir: -1 }, name: st.eyes[1] ?? st.eyes[0] });
  } else geo.eyes.forEach((g, k) => eyeList.push({ g, name: (k === 1 ? st.eyes[1] : undefined) ?? st.eyes[0] }));
  for (const { g, name } of eyeList) {
    // Head-on, gaze moves both irises the same way on screen (the left eye's own gx is mirrored back).
    const s0 = EYE[name] ?? EYE.open!;
    const s = view === 'front' && g.dir === -1 && s0.gx ? { ...s0, gx: s0.gx } : s0;
    const px = eyePx(g, s, sex, geo.gaze, left);
    for (const p of px) if (p.ch === 'K') tops.set(p.x, Math.min(tops.get(p.x) ?? 99, p.y));
    eyes.push(...px);
  }
  const brows: { x: number; y: number; ch: string }[] = [];
  const browList: BrowGeo[] = view === 'front' ? [geo.brows[0]!, { ...geo.brows[0]!, x0: -1 - geo.brows[0]!.x0, dir: -1 }] : geo.brows;
  browList.forEach((g, k) => {
    const alt = st.brow.alt && k === 1 ? { ...st.brow, ...st.brow.alt } : st.brow;
    brows.push(...browPx(g, alt, sex, tops));
  });
  const shape = MOUTH[st.mouth]?.[sex] ?? MOUTH.calm![sex];
  const mouth = mouthPx(shape, view, geo.mouth, left);
  const extra: { x: number; y: number; ch: string }[] = [];
  for (const x of st.extra ?? []) {
    if (x === 'blush')
      for (const g of eyeList) {
        if (g.g.far && view !== 'front') continue;
        const cx = g.g.x0 + g.g.dir * Math.floor(g.g.w / 2);
        const y = g.g.top + g.g.open + 3;
        // A solid 4 px strip under the eye (review r1: the hatched 2-row blush read as a checkerboard).
        for (let k = -2; k <= 1; k++) extra.push({ x: cx + k, y: y - 1, ch: 'h' });
      }
    if (x === 'tear') {
      const g = eyeList[0]!.g;
      const ox = g.x0 + g.dir * (g.w - 1);
      extra.push({ x: ox, y: g.top + g.open + 1, ch: 'T' }, { x: ox, y: g.top + g.open + 2, ch: 'T' });
    }
  }
  const paint = (p: { x: number; y: number; ch: string }): FPx => ({ x: p.x, y: p.y, col: col[p.ch as keyof Colours] ?? col.K });
  return { eyesBrows: [...extra, ...brows, ...eyes].map(paint), mouth: mouth.map(paint), blush: !!st.extra?.includes('blush') };
}
